import React from 'react';
import { Box, Typography, Chip, Divider } from '@mui/material';
import {
  TrendingUp, TrendingDown, TrendingFlat,
  AttachMoney, ShoppingCart, Savings, AccessTime,
  PieChart, HourglassTop, BarChart, Factory,
  CheckCircleOutline, Warning,
} from '@mui/icons-material';
import { DashboardWidget, WidgetDataResult } from '../../../redux/wallboardApi';
import { useDashboardContext, WIDGET_COLORS } from '../pages/DashboardViewPage';

/** Map metric IDs to a recognisable icon. */
function getMetricIcon(metric: string) {
  const map: Record<string, React.ElementType> = {
    total_po_value:      AttachMoney,
    po_count:            ShoppingCart,
    bom_savings:         Savings,
    approval_cycle_time: AccessTime,
    budget_utilization:  PieChart,
    pending_approvals:   HourglassTop,
    requisition_volume:  BarChart,
    supplier_spend:      Factory,
    delivery_performance: CheckCircleOutline,
    payment_status:      Warning,
  };
  return map[metric] || AttachMoney;
}

/**
 * Scorecard Widget
 * 
 * Displays a single KPI value with optional trend indicator.
 * Supports dynamic font sizing and themed colors.
 */
interface ScorecardWidgetProps {
  widget: DashboardWidget;
  data?: WidgetDataResult;
  colorIndex?: number;
}

export const ScorecardWidget: React.FC<ScorecardWidgetProps> = ({ widget, data, colorIndex = 0 }) => {
  const displayOptions = widget.config?.displayOptions || {};
  const unit = data?.metadata?.unit || widget.config?.unit || '';
  const metricType = data?.metadata?.metricType || '';
  const { fontSize, onDrillDown } = useDashboardContext();
  
  // Get color theme based on index
  const colorTheme = WIDGET_COLORS[colorIndex % WIDGET_COLORS.length];
  // Use valueColor for better contrast against white background, fall back to header
  const themeColor = colorTheme.valueColor || colorTheme.header;
  
  // Get value from data - ensure it's a number
  const rawValue = data?.data?.value ?? data?.data ?? 0;
  const value = typeof rawValue === 'number' ? rawValue : Number(rawValue) || 0;
  const count = data?.data?.count;
  const trend = data?.trend;

  // Format value based on metric type and unit
  const formatValue = (val: number): string => {
    const numVal = typeof val === 'number' ? val : Number(val) || 0;
    if (displayOptions.prefix || displayOptions.suffix) {
      const prefix = displayOptions.prefix || '';
      const suffix = displayOptions.suffix || '';
      return `${prefix}${formatNumber(numVal)}${suffix}`;
    }
    
    // Currency formatting
    if (unit === '$' || metricType === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: displayOptions.decimalPlaces ?? 0,
      }).format(numVal);
    }
    
    // Percentage formatting
    if (unit === '%' || metricType === 'percentage') {
      return `${formatNumber(numVal)}%`;
    }
    
    // Duration formatting (days)
    if (metricType === 'duration') {
      const unitLabel = unit || 'days';
      return `${formatNumber(numVal)} ${unitLabel}`;
    }
    
    // Count formatting - no decimals, no unit suffix
    if (metricType === 'count') {
      return formatNumber(numVal, 0);
    }
    
    return `${formatNumber(numVal)}${unit ? ` ${unit}` : ''}`;
  };

  const formatNumber = (val: number, overrideDecimals?: number): string => {
    const numVal = typeof val === 'number' ? val : Number(val) || 0;
    const decimals = overrideDecimals ?? displayOptions.decimalPlaces ?? (metricType === 'count' ? 0 : 2);
    
    if (numVal >= 1000000) {
      return `${(numVal / 1000000).toFixed(decimals > 2 ? 2 : decimals)}M`;
    }
    if (numVal >= 1000) {
      return `${(numVal / 1000).toFixed(decimals > 1 ? 1 : decimals)}K`;
    }
    return numVal.toFixed(decimals);
  };

  // Trend colors
  const getTrendColor = () => {
    if (!trend) return 'inherit';
    return trend.direction === 'up' ? 'success.main' : 
           trend.direction === 'down' ? 'error.main' : 'text.secondary';
  };

  const TrendIcon = trend?.direction === 'up' ? TrendingUp :
                    trend?.direction === 'down' ? TrendingDown : TrendingFlat;

  const MetricIcon = getMetricIcon(widget.config?.metric || '');

  return (
    <Box
      onClick={() => onDrillDown(widget.id, widget.name)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
      }}
    >
      {/* Icon + Value row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Contextual icon */}
        <Box
          sx={{
            width: 40 * fontSize,
            height: 40 * fontSize,
            borderRadius: '50%',
            bgcolor: `${displayOptions.textColor || themeColor}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MetricIcon
            sx={{
              fontSize: 22 * fontSize,
              color: displayOptions.textColor || themeColor,
            }}
          />
        </Box>

        {/* Primary value */}
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            color: displayOptions.textColor || themeColor,
            fontSize: `${2.2 * fontSize}rem`,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {formatValue(value)}
        </Typography>
      </Box>

      {/* Divider */}
      <Divider sx={{ my: 0.75, borderColor: `${themeColor}22` }} />

      {/* Trend row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
        {trend ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendIcon
              sx={{
                color: getTrendColor(),
                fontSize: 16 * fontSize,
              }}
            />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: getTrendColor(), fontSize: `${0.8 * fontSize}rem` }}
            >
              {trend.percentage > 0 ? '+' : ''}{trend.percentage}%
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: `${0.7 * fontSize}rem` }}
            >
              vs prev. period
            </Typography>
          </Box>
        ) : (
          <Box /> /* spacer */
        )}

        {/* Count badge */}
        {count !== undefined && count > 0 && (
          <Chip
            label={`${count.toLocaleString()} items`}
            size="small"
            variant="outlined"
            sx={{
              height: 18,
              fontSize: `${0.65 * fontSize}rem`,
              borderColor: `${themeColor}44`,
              color: themeColor,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ScorecardWidget;
